import { colors, Stack, Typography } from '@ui-components/neo.tax';
import { MenuItem as AriaMenuItem } from 'ariakit';
import dayjs from 'dayjs';
import React, { ReactElement } from 'react';

import { BackgroundOperationName } from '@neo.tax/shared/graphql';
import { BackgroundOperation } from '@neo.tax/shared/hooks/useBackgroundOperations';

import { NotificationTitle } from './notification-menu.styled';

const notificationDisplayName: Record<BackgroundOperationName, string> = {
  [BackgroundOperationName.BulkRecalculateAscAllocationsOperation]: 'Recalculating ASC allocations',
  [BackgroundOperationName.BulkRecalculateRdAllocationsOperation]: 'Recalculating R&D allocations',
  [BackgroundOperationName.GenerateAscReportOperation]: 'Generating ASC report',
  [BackgroundOperationName.GenerateFederalRdWriteupOperation]: 'Generating Federal R&D write-up',
  [BackgroundOperationName.GenerateRdFromTimesheetsOperation]: 'Generating R&D from timesheets',
  [BackgroundOperationName.GenerateRdProjectsReportOperation]: 'Generating R&D projects report',
  [BackgroundOperationName.SyncTicketingDataOperation]: 'Syncing ticketing data and generating projects',
  [BackgroundOperationName.ResyncTicketingDataOperation]: 'Re-syncing ticketing data and generating projects',
  [BackgroundOperationName.ValidateTicketingFileOperation]: 'Validating ticketing file',
  [BackgroundOperationName.ImportTimesheetFileOperation]: 'Importing timesheet file',
  [BackgroundOperationName.ImportAscCostFileOperation]: 'Importing ASC cost file',
  [BackgroundOperationName.ImportRdCostFileOperation]: 'Importing R&D cost file',
  [BackgroundOperationName.MergeRdProjectsOperation]: 'Merging R&D projects',
  [BackgroundOperationName.ApplyRdSupervisorAllocationOperation]: 'Applying supervisor R&D allocation',
  [BackgroundOperationName.ImportAscProjectsFileOperation]: 'Bulk creating ASC projects',
  [BackgroundOperationName.ImportRdProjectsFileOperation]: 'Bulk creating R&D projects',
  [BackgroundOperationName.ImportRdBusinessComponentsFileOperation]: 'Bulk creating R&D business components',
  [BackgroundOperationName.ApplyGithubEmailMappingOperation]: 'Applying GitHub email mapping',
  [BackgroundOperationName.RegenerateRdProjectDetailsOperation]: 'Updating R&D project details',
};

const NotificationMenuItem = ({ item }: { item: BackgroundOperation }): ReactElement => (
  <AriaMenuItem>
    <Stack paddingY={3} paddingX={3} fullWidth>
      <Stack fullWidth alignItems="center" justifyContent="space-between">
        <Stack direction="column" alignItems="start" justifyContent="start" gap={1}>
          <NotificationTitle>
            <Typography fontSize="xs" fontWeight="medium" color={colors.gray[600]}>
              {notificationDisplayName[item.name]}
            </Typography>
          </NotificationTitle>
          <Typography fontSize="2xs" color={colors.gray[500]}>
            Started on {dayjs(item.createdAt).format('MM/DD/YY')}
          </Typography>
        </Stack>
        <Stack direction="column" alignItems="end" justifyContent="center">
          {item.progressPercent === 0 ? (
            <Typography fontSize="xs" color={colors.gray[500]}>
              In queue
            </Typography>
          ) : item.progressPercent === 100 ? (
            <Typography fontSize="xs" color={colors.primary[600]}>
              Completed
            </Typography>
          ) : (
            <>
              <Typography as="div" textAlign="right" fontSize="xs" color={colors.blue[600]}>
                {item.progressPercent}%
              </Typography>
              <Typography as="div" textAlign="right" fontSize="xs" color={colors.blue[600]}>
                complete
              </Typography>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  </AriaMenuItem>
);

export const MemoizedNotificationMenuItem = React.memo(NotificationMenuItem) as typeof NotificationMenuItem;
